'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useRouter } from '@/i18n/routing';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { authApi } from '@/features/auth/services/auth.api';
import { AUTH_STORAGE_KEYS, AUTH_COOKIE_NAME } from '@/features/auth/constants';
import { toAuthUser } from '@/features/auth/mappers/auth.mapper';
import { isOtpRequired } from '@/features/auth/types';
import {
  capturePendingRedirect,
  clearPendingRedirect,
  markExplicitLogout
} from '@/features/auth/utils/redirect';
import type {
  AuthUser,
  AuthStatus,
  LoginRequest,
  LoginResponse,
  VerifyLoginOtpResponse
} from '@/features/auth/types';

interface AuthContextType {
  user: AuthUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  otpEmail: string | null;
  otpExpiresAt: string | null;
  otpResendAvailableAt: string | null;
  login(credentials: LoginRequest): Promise<LoginResponse>;
  verifyOtp(otp: string): Promise<void>;
  completeLogin(data: VerifyLoginOtpResponse): void;
  logout(): void;
  clearSession(): void;
  updatePermissions(roles: string[], permissions: string[]): void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function removeCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [otpEmail, setOtpEmail] = useState<string | null>(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | null>(null);
  const [otpResendAvailableAt, setOtpResendAvailableAt] = useState<
    string | null
  >(null);

  const loginMutation = useMutation({
    mutationFn: authApi.login
  });

  const verifyOtpMutation = useMutation({
    mutationFn: authApi.verifyLoginOtp
  });

  const clearSession = useCallback(() => {
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(AUTH_STORAGE_KEYS.OTP_EMAIL);
    localStorage.removeItem(AUTH_STORAGE_KEYS.OTP_EXPIRES_AT);
    localStorage.removeItem(AUTH_STORAGE_KEYS.OTP_RESEND_AVAILABLE_AT);
    removeCookie(AUTH_COOKIE_NAME);
    apiClient.removeAuthToken();
    apiClient.resetPermissionsHash();
    setUser(null);
    setOtpEmail(null);
    setOtpExpiresAt(null);
    setOtpResendAvailableAt(null);
    setStatus('unauthenticated');
  }, []);

  const updatePermissions = useCallback(
    (roles: string[], permissions: string[]) => {
      setUser((prev) => {
        if (!prev) return prev;

        const rolesChanged =
          prev.roles.length !== roles.length ||
          prev.roles.some((r, i) => r !== roles[i]);
        const permsChanged =
          prev.permissions.length !== permissions.length ||
          prev.permissions.some((p, i) => p !== permissions[i]);

        if (!rolesChanged && !permsChanged) return prev;

        const updated = { ...prev, roles, permissions };
        localStorage.setItem(
          AUTH_STORAGE_KEYS.USER_DATA,
          JSON.stringify(updated)
        );
        return updated;
      });
    },
    []
  );

  const completeLogin = useCallback((data: VerifyLoginOtpResponse) => {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, data.token);
    localStorage.setItem(
      AUTH_STORAGE_KEYS.USER_DATA,
      JSON.stringify(data.user)
    );
    localStorage.removeItem(AUTH_STORAGE_KEYS.OTP_EMAIL);
    localStorage.removeItem(AUTH_STORAGE_KEYS.OTP_EXPIRES_AT);
    localStorage.removeItem(AUTH_STORAGE_KEYS.OTP_RESEND_AVAILABLE_AT);
    // Drop any legacy JS-set copy; nothing reads this cookie.
    removeCookie(AUTH_COOKIE_NAME);
    apiClient.setAuthToken(data.token);

    const authUser = toAuthUser(data);
    setUser(authUser);
    setOtpEmail(null);
    setOtpExpiresAt(null);
    setOtpResendAvailableAt(null);
    setStatus('authenticated');
  }, []);

  // Initialize from stored token
  useEffect(() => {
    // Legacy: older builds mirrored the token into a JS-readable cookie.
    removeCookie(AUTH_COOKIE_NAME);
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    const userData = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData) as AuthUser;
        apiClient.setAuthToken(token);
        setUser(parsedUser);
        setStatus('authenticated');
      } catch {
        clearSession();
      }
    } else if (token) {
      clearSession();
    } else {
      const storedOtpEmail = localStorage.getItem(AUTH_STORAGE_KEYS.OTP_EMAIL);
      if (storedOtpEmail) {
        setOtpEmail(storedOtpEmail);
        setOtpExpiresAt(localStorage.getItem(AUTH_STORAGE_KEYS.OTP_EXPIRES_AT));
        setOtpResendAvailableAt(
          localStorage.getItem(AUTH_STORAGE_KEYS.OTP_RESEND_AVAILABLE_AT)
        );
        setStatus('otp_pending');
      } else {
        setStatus('unauthenticated');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest): Promise<LoginResponse> => {
      const response = await loginMutation.mutateAsync(credentials);
      const data = response.data;

      // Login always requires OTP with this backend
      setOtpEmail(credentials.email);
      localStorage.setItem(AUTH_STORAGE_KEYS.OTP_EMAIL, credentials.email);

      if (isOtpRequired(data)) {
        setOtpExpiresAt(data.expires_at);
        setOtpResendAvailableAt(data.resend_available_at);
        localStorage.setItem(AUTH_STORAGE_KEYS.OTP_EXPIRES_AT, data.expires_at);
        localStorage.setItem(
          AUTH_STORAGE_KEYS.OTP_RESEND_AVAILABLE_AT,
          data.resend_available_at
        );
      }

      setStatus('otp_pending');
      return data;
    },
    [loginMutation]
  );

  const verifyOtp = useCallback(
    async (otp: string): Promise<void> => {
      const email =
        otpEmail || localStorage.getItem(AUTH_STORAGE_KEYS.OTP_EMAIL);
      if (!email) throw new Error('Email not found for OTP verification.');

      const response = await verifyOtpMutation.mutateAsync({ email, otp });
      completeLogin(response.data);
    },
    [otpEmail, verifyOtpMutation, completeLogin]
  );

  // Shared helper: fetch permissions, update state, and invalidate query cache
  const refreshPermissions = useCallback(
    async (resetHashOnError: boolean) => {
      try {
        const res = await authApi.fetchMyPermissions();
        updatePermissions(res.data.roles, res.data.permissions);
        queryClient.invalidateQueries();
      } catch {
        if (resetHashOnError) {
          apiClient.resetPermissionsHash();
        }
      }
    },
    [updatePermissions, queryClient]
  );

  // Sync permissions when API client detects hash change
  useEffect(() => {
    if (status !== 'authenticated') return;

    const handlePermissionsChanged = () => {
      refreshPermissions(true);
    };

    window.addEventListener('permissions-changed', handlePermissionsChanged);
    return () => {
      window.removeEventListener(
        'permissions-changed',
        handlePermissionsChanged
      );
    };
  }, [status, refreshPermissions]);

  // Refresh permissions when user returns to the tab
  useEffect(() => {
    if (status !== 'authenticated') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPermissions(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, refreshPermissions]);

  // Poll for permission changes every 3 minutes (proactive refresh for idle users)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (status !== 'authenticated') {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    pollingRef.current = setInterval(
      () => {
        if (document.visibilityState === 'visible' && navigator.onLine) {
          refreshPermissions(false);
        }
      },
      3 * 60 * 1000
    );

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [status, refreshPermissions]);

  const logout = useCallback(() => {
    // Order matters: clearing wipes the marker, so mark the sign-out after it.
    clearPendingRedirect();
    markExplicitLogout();
    queryClient.clear();
    clearSession();
    router.push('/login');
  }, [clearSession, queryClient, router]);

  // Auto-logout when token refresh fails (session expired)
  useEffect(() => {
    const handleSessionExpired = () => {
      capturePendingRedirect();
      queryClient.clear();
      clearSession();
      router.push('/login');
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [clearSession, queryClient, router]);

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        isAuthenticated: status === 'authenticated',
        otpEmail,
        otpExpiresAt,
        otpResendAvailableAt,
        login,
        verifyOtp,
        completeLogin,
        logout,
        clearSession,
        updatePermissions
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
